import React, { useEffect, useMemo, useRef, useState } from 'react'
import { debounce } from 'src/lib/debounce'
import dompurify from 'dompurify'
import { DESCRIPTION_SANITIZATION_CONFIG } from '../constants'
import { injectIntl, InjectedIntlProps } from 'react-intl'
import locale from '../locale'

const style = require('./CollapsibleHTMLContent.css')

const DEFAULT_COLLAPSE_HEIGHT = 250;

interface CollapsibleHTMLContentProps {
  htmlContent: string | null
  htmlSanitizationConfig: any
  isMobile: Boolean
  maxHeight?: number
  alwaysCollapseAtMaxHeight?: boolean
}

const CollapsibleHTMLContentComponent: React.FunctionComponent<CollapsibleHTMLContentProps & InjectedIntlProps> = ({
  htmlContent,
  htmlSanitizationConfig,
  isMobile,
  maxHeight = DEFAULT_COLLAPSE_HEIGHT,
  alwaysCollapseAtMaxHeight = false,
  intl: { formatMessage }
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [showExpandButton, setShowExpandButton] = useState(false)
  const contentRef = useRef<HTMLDivElement | null>(null)

  const className = useMemo(() => showExpandButton && !isExpanded
      ? `${style.container} ${style.collapsed}`
      : style.container,
    [showExpandButton, isExpanded]);

  const buttonText = () =>
    isExpanded ? formatMessage(locale.showLess) : formatMessage(locale.showMore);
  const getHeight = () => contentRef.current?.clientHeight ?? 0

  const shouldCollapse = () => {
    const height = getHeight()
    return (isMobile || alwaysCollapseAtMaxHeight) && height >= maxHeight
  }

  // Initialize collapse state when content or collapse-related props change.
  // This resets both the button visibility AND expanded state since we're
  // treating it as "new" content that should start in its default state.
  useEffect(() => {
    const collapse = shouldCollapse()
    setShowExpandButton(collapse)
    setIsExpanded(!collapse)
  }, [htmlContent, isMobile, alwaysCollapseAtMaxHeight, maxHeight])

  // Re-evaluate button visibility on window resize (content may reflow).
  // Intentionally does NOT reset isExpanded — if the user clicked "show more",
  // we preserve that choice even when mobile browsers fire resize events
  // (e.g., Safari's address bar showing/hiding during scroll).
  useEffect(() => {
    const handler = debounce(() => {
      setShowExpandButton(shouldCollapse())
    }, 100)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [isMobile, alwaysCollapseAtMaxHeight, maxHeight, htmlContent])

  useEffect(() => {
    // https://github.com/cure53/DOMPurify/tree/main/demos#hook-to-open-all-links-in-a-new-window-link
    dompurify.addHook('afterSanitizeAttributes', node => {
      if ('target' in node) node.setAttribute('target', '_blank');
    });
  }, []);

  return (
    <>
      <div
        className={className}
        ref={contentRef}
        dangerouslySetInnerHTML={{
          __html: dompurify.sanitize(htmlContent, htmlSanitizationConfig)
        }} // eslint-disable-line react/no-danger
        style={!isExpanded && showExpandButton ? { maxHeight } : undefined}
      />
      {
        showExpandButton &&
          <button className={style.button} onClick={() => setIsExpanded(prev => !prev)}>
            {buttonText()}
          </button>
      }
    </>
  );
}

export const CollapsibleHTMLContent = injectIntl(CollapsibleHTMLContentComponent);
